import { useEffect, useState } from 'react'
import { NavLink, useParams } from 'react-router-dom'
import { Link } from 'react-router-dom'
import { api } from '../api/client.ts'
import { Avatar } from '../components/Avatar.tsx'
import { CommentsPanel } from '../components/CommentsPanel.tsx'
import { SendToChatDialog } from '../components/SendToChatDialog.tsx'

/**
 * ВИШ-ЛИСТЫ
 *
 * Свой список — это «что мне подарить», чужой — «что подарить ему».
 * Поэтому и выглядят они по-разному: в своём видно, что ты попросил,
 * в чужом — что уже разобрали.
 *
 * Хозяин списка не видит броней вообще: сервер их ему не отдаёт.
 * Здесь про это помнить не нужно — но нужно не завести случайно
 * подсказку вроде «этот подарок уже кто-то смотрел».
 */

type WishList = {
  id: string
  name: string
  occasion: string | null
  owner: { id: string; displayName: string; avatarUrl: string | null }
  mine: boolean
  total: number
  reserved: number | null
}

type Wish = {
  id: string
  title: string
  url: string | null
  price: number | null
  note: string | null
  priority: number
  reserved: boolean
  reservedBy: { id: string; displayName: string; avatarUrl: string | null } | null
  reservedByMe: boolean
  purchased: boolean
}

export function WishesPage() {
  const [lists, setLists] = useState<WishList[] | null>(null)
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [occasion, setOccasion] = useState('')
  const [error, setError] = useState<string | null>(null)

  const load = () => {
    api<{ lists: WishList[] }>('/api/wishlists')
      .then((data) => setLists(data.lists))
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить'))
  }

  useEffect(load, [])

  const create = async () => {
    if (!name.trim()) return
    try {
      await api('/api/wishlists', {
        method: 'POST',
        body: { name: name.trim(), occasion: occasion.trim() || null },
      })
      setName('')
      setOccasion('')
      setCreating(false)
      load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось создать')
    }
  }

  const mine = (lists ?? []).filter((l) => l.mine)
  const others = (lists ?? []).filter((l) => !l.mine)

  return (
    <div className="section">
      <header className="section-head">
        <h1>Желания</h1>
        <button className="icon-btn" onClick={() => setCreating(true)} aria-label="Новый список">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
            <path d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2z" />
          </svg>
        </button>
      </header>

      <div className="section-body scroll">
        {lists?.length === 0 && (
          <p className="empty-hint">
            Списков нет. Заведи свой — родные перестанут гадать, что дарить.
          </p>
        )}

        {others.length > 0 && (
          <>
            <h3 className="sheet-sub">Кому что подарить</h3>
            <div className="card-grid">
              {others.map((list) => (
                <NavLink key={list.id} to={`/wishes/${list.id}`} className="list-card">
                  <div className="list-card-top">
                    <Avatar
                      id={list.owner.id}
                      name={list.owner.displayName}
                      url={list.owner.avatarUrl}
                      size={24}
                    />
                    <span className="list-card-name">{list.owner.displayName}</span>
                  </div>

                  <div className="list-card-store">{list.name}</div>

                  <div className="list-card-progress">
                    <div
                      className="list-card-fill"
                      style={{
                        width: list.total ? `${((list.reserved ?? 0) / list.total) * 100}%` : '0%',
                      }}
                    />
                    <span className="list-card-count">
                      {list.total === 0
                        ? 'пусто'
                        : `разобрали ${list.reserved ?? 0} из ${list.total}`}
                    </span>
                  </div>
                </NavLink>
              ))}
            </div>
          </>
        )}

        {mine.length > 0 && (
          <>
            <h3 className="sheet-sub">Мои списки</h3>
            <div className="card-grid">
              {mine.map((list) => (
                <NavLink key={list.id} to={`/wishes/${list.id}`} className="list-card">
                  <div className="list-card-top">
                    <span className="list-card-icon" aria-hidden="true">
                      🎁
                    </span>
                    <span className="list-card-name">{list.name}</span>
                  </div>
                  {list.occasion && <div className="list-card-store">{list.occasion}</div>}
                  <div className="list-card-store">
                    {list.total === 0 ? 'пусто' : `желаний: ${list.total}`}
                  </div>
                </NavLink>
              ))}
            </div>
          </>
        )}

        {error && <div className="sheet-error">{error}</div>}
      </div>

      {creating && (
        <div className="sheet" onClick={() => setCreating(false)}>
          <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
            <div className="sheet-head">
              <h2>Новый список желаний</h2>
              <button className="icon-btn" onClick={() => setCreating(false)} aria-label="Закрыть">
                ✕
              </button>
            </div>

            <label className="field">
              <span className="field-label">Название</span>
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Хотелки, На день рождения"
                maxLength={64}
                autoFocus
              />
            </label>

            <label className="field">
              <span className="field-label">По какому поводу</span>
              <input
                className="input"
                value={occasion}
                onChange={(e) => setOccasion(e.target.value)}
                placeholder="Не обязательно"
                maxLength={64}
              />
            </label>

            <button className="btn-primary" onClick={() => void create()} disabled={!name.trim()}>
              Создать
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export function WishListPage() {
  const { listId } = useParams<{ listId: string }>()
  const [data, setData] = useState<{ list: WishList; items: Wish[] } | null>(null)
  const [draft, setDraft] = useState<Partial<Wish> | null>(null)
  const [open, setOpen] = useState<Wish | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = () => {
    if (!listId) return
    api<{ list: WishList; items: Wish[] }>(`/api/wishlists/${listId}`)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Список не найден'))
  }

  useEffect(load, [listId])

  const reserve = async (item: Wish, reserved: boolean) => {
    try {
      await api(`/api/wishlists/items/${item.id}/reserve`, {
        method: 'POST',
        body: { reserved },
      })
      load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось')
    }
  }

  if (error) {
    return (
      <div className="section">
        <div className="center-screen">
          <p className="empty-hint">{error}</p>
        </div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="section">
        <div className="center-screen">
          <div className="spinner" />
        </div>
      </div>
    )
  }

  return (
    <div className="section">
      <header className="section-head">
        <Link to="/wishes" className="icon-btn" aria-label="Назад">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
            <path d="M15.4 7.4 14 6l-6 6 6 6 1.4-1.4-4.6-4.6z" />
          </svg>
        </Link>

        <div className="section-title">
          <h1>{data.list.mine ? data.list.name : `Что подарить: ${data.list.owner.displayName}`}</h1>
          {data.list.occasion && <span className="section-sub">{data.list.occasion}</span>}
        </div>

        {data.list.mine && (
          <button className="icon-btn" onClick={() => setDraft({ title: '' })} aria-label="Добавить">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
              <path d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2z" />
            </svg>
          </button>
        )}
      </header>

      <div className="section-body scroll">
        {data.items.length === 0 && (
          <p className="empty-hint">
            {data.list.mine
              ? 'Пусто. Добавь то, что хочется, — со ссылкой на магазин.'
              : 'Список пока пуст.'}
          </p>
        )}

        {!data.list.mine && data.items.length > 0 && (
          <p className="sheet-note">
            Хозяин списка не видит, что разобрали: сюрприз остаётся сюрпризом.
          </p>
        )}

        {data.items.map((item) => (
          <div key={item.id} className={'wish-row' + (item.reserved ? ' is-reserved' : '')}>
            <button className="wish-main" onClick={() => setOpen(item)}>
              <span className="wish-text">
                <span className="wish-title">
                  {item.priority > 0 && <span className="task-flag">!</span>}
                  {item.title}
                </span>
                <span className="wish-sub">
                  {item.price !== null && <span>{item.price.toLocaleString('ru-RU')} ₽</span>}
                  {item.note && <span>{item.note}</span>}
                  {item.reserved && item.reservedBy && (
                    <span className="wish-taken">
                      {item.reservedByMe ? 'беру я' : `берёт ${item.reservedBy.displayName}`}
                      {item.purchased && ' · куплено'}
                    </span>
                  )}
                </span>
              </span>
            </button>

            {item.url && (
              <a
                className="wish-link"
                href={item.url}
                target="_blank"
                rel="noreferrer"
                title="Открыть в магазине"
              >
                ↗
              </a>
            )}

            {!data.list.mine && (
              <button
                className={'rsvp-btn' + (item.reservedByMe ? ' is-yes' : '')}
                onClick={() => void reserve(item, !item.reservedByMe)}
                disabled={item.reserved && !item.reservedByMe}
                title={item.reservedByMe ? 'Отказаться' : 'Беру на себя'}
              >
                {item.reserved ? '✓' : '+'}
              </button>
            )}
          </div>
        ))}
      </div>

      {draft && (
        <WishSheet
          draft={draft}
          listId={listId!}
          onClose={() => setDraft(null)}
          onSaved={load}
        />
      )}

      {open && (
        <WishDetails
          item={open}
          mine={data.list.mine}
          onClose={() => setOpen(null)}
          onEdit={() => {
            setDraft(open)
            setOpen(null)
          }}
        />
      )}
    </div>
  )
}

function WishDetails({
  item,
  mine,
  onClose,
  onEdit,
}: {
  item: Wish
  mine: boolean
  onClose: () => void
  onEdit: () => void
}) {
  const [sending, setSending] = useState(false)

  return (
    <div className="sheet" onClick={onClose}>
      <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h2>{item.title}</h2>
          <button className="icon-btn" onClick={() => setSending(true)} title="Отправить в переписку">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M3 20.5 21 12 3 3.5 3 10l12 2-12 2z" />
            </svg>
          </button>
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>

        {item.price !== null && (
          <p className="sheet-text">Примерно {item.price.toLocaleString('ru-RU')} ₽</p>
        )}
        {item.note && <p className="sheet-text">{item.note}</p>}

        {item.url && (
          <p className="sheet-text">
            <a className="auth-link" href={item.url} target="_blank" rel="noreferrer">
              Открыть в магазине
            </a>
          </p>
        )}

        {/* Обсуждение подарка хозяину списка не видно вместе с ним самим:
            он не увидит здесь ничего, потому что не заходит в чужую бронь */}
        {!mine && (
          <>
            <h3 className="sheet-sub">Сговор дарителей</h3>
            <p className="sheet-note">Хозяин списка этого обсуждения не видит.</p>
            <CommentsPanel entityType="WISH_ITEM" entityId={item.id} />
          </>
        )}

        {mine && (
          <div className="sheet-actions">
            <button className="btn-primary" onClick={onEdit}>
              Изменить
            </button>
          </div>
        )}

        {sending && (
          <SendToChatDialog
            entityType="WISH_ITEM"
            entityId={item.id}
            title={item.title}
            onClose={() => setSending(false)}
          />
        )}
      </div>
    </div>
  )
}

function WishSheet({
  draft,
  listId,
  onClose,
  onSaved,
}: {
  draft: Partial<Wish>
  listId: string
  onClose: () => void
  onSaved: () => void
}) {
  const editing = Boolean(draft.id)

  const [title, setTitle] = useState(draft.title ?? '')
  const [url, setUrl] = useState(draft.url ?? '')
  const [price, setPrice] = useState(draft.price?.toString() ?? '')
  const [note, setNote] = useState(draft.note ?? '')
  const [important, setImportant] = useState((draft.priority ?? 0) > 0)

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const save = async () => {
    setBusy(true)
    setError(null)

    try {
      const body = {
        title: title.trim(),
        url: url.trim() || null,
        price: price.trim() ? Number(price.replace(',', '.')) : null,
        note: note.trim() || null,
        priority: important ? 2 : 0,
      }

      if (draft.id) {
        await api(`/api/wishlists/items/${draft.id}`, { method: 'PATCH', body })
      } else {
        await api(`/api/wishlists/${listId}/items`, { method: 'POST', body })
      }

      onSaved()
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не сохранилось')
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!draft.id || !window.confirm('Убрать из списка?')) return
    setBusy(true)
    try {
      await api(`/api/wishlists/items/${draft.id}`, { method: 'DELETE' })
      onSaved()
      onClose()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sheet" onClick={onClose}>
      <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h2>{editing ? 'Желание' : 'Что хочется'}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>

        <label className="field">
          <span className="field-label">Что</span>
          <input
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Наушники, книга, сковорода"
            maxLength={200}
            autoFocus={!editing}
          />
        </label>

        <label className="field">
          <span className="field-label">Ссылка на товар</span>
          <input
            className="input"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="Чтобы не искали и не ошиблись моделью"
            maxLength={1000}
          />
        </label>

        <label className="field">
          <span className="field-label">Примерная цена</span>
          <input
            className="input"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="Не обязательно"
            inputMode="decimal"
          />
        </label>

        <label className="check-row">
          <input
            type="checkbox"
            checked={important}
            onChange={(e) => setImportant(e.target.checked)}
          />
          <span>Хочется больше всего</span>
        </label>

        <label className="field">
          <span className="field-label">Уточнение</span>
          <input
            className="input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Цвет, размер, «только не синие»"
            maxLength={1000}
          />
        </label>

        {error && <div className="sheet-error">{error}</div>}

        <div className="sheet-actions">
          <button className="btn-primary" disabled={busy || !title.trim()} onClick={() => void save()}>
            {busy ? '…' : 'Сохранить'}
          </button>
          {editing && (
            <button className="btn-quiet" disabled={busy} onClick={() => void remove()}>
              Убрать
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
